import { BehaviorSubject, Observable, Subscription } from 'rxjs';

/**
 * Distributes one global observable to many receivers with O(1) subscribe and unsubscribe.
 *
 * Every layout element used to subscribe to the same global subjects (permissions, overlay,
 * theme, language, delete). RxJS keeps the observers of a subject in an array and removes an
 * observer by a linear search -- with thousands of rows in a repeat, tearing a page down became
 * quadratic (profiled: `arrRemove` ~0.4 s self time at 8 420 rows).
 *
 * The distributor subscribes to the source **once** and keeps its receivers in a Set. It behaves
 * like a subscription from the receiver's point of view:
 * - a BehaviorSubject's current value is delivered immediately on registration,
 * - receivers are called in registration order,
 * - a throwing receiver does not stop the others; the error is reported asynchronously, as
 *   RxJS 7 does for errors in `next` handlers,
 * - registration returns an object with `unsubscribe()`, so existing `ngOnDestroy` code that
 *   unsubscribes everything in `Subscriptions` keeps working unchanged.
 */
export class Verteiler<T> {
    private static readonly _Instanzen = new WeakMap<object, Verteiler<any>>();

    private readonly _Empfaenger = new Set<(wert: T) => void>();
    private _Quelle: Subscription = null;

    private constructor(private readonly quelle: Observable<T>) { }

    /** The distributor for a source -- one per source, created on first use. */
    static fuer<T>(quelle: Observable<T>): Verteiler<T> {
        let verteiler = Verteiler._Instanzen.get(quelle);
        if (!verteiler) {
            verteiler = new Verteiler<T>(quelle);
            Verteiler._Instanzen.set(quelle, verteiler);
        }
        return verteiler;
    }

    /** Registers a receiver. The result's `unsubscribe()` removes it again in O(1). */
    anmelden(empfaenger: (wert: T) => void): { unsubscribe(): void; closed: boolean } {
        this._Empfaenger.add(empfaenger);
        const abmeldung = {
            closed: false,
            unsubscribe: () => {
                abmeldung.closed = true;
                this._Empfaenger.delete(empfaenger);
            }
        };
        if (this._Quelle == null) {
            // Subscribing late keeps a BehaviorSubject from replaying to an empty set; the
            // current value goes to this first receiver below like to every later one.
            this._Quelle = this.quelle.subscribe(wert => this.verteile(wert));
        } else if (this.quelle instanceof BehaviorSubject) {
            Verteiler.rufe(empfaenger, this.quelle.getValue());
        }
        return abmeldung;
    }

    private verteile(wert: T) {
        // A copy, so a receiver that unsubscribes others during delivery does not change the
        // round -- RxJS also delivers to the observers that were present when `next` ran.
        for (const empfaenger of Array.from(this._Empfaenger)) {
            Verteiler.rufe(empfaenger, wert);
        }
    }

    private static rufe<T>(empfaenger: (wert: T) => void, wert: T) {
        try {
            empfaenger(wert);
        } catch (fehler) {
            setTimeout(() => { throw fehler; });
        }
    }
}
